import { actions } from '@ecosystem/newron-design-system';
import React, { forwardRef, useState, useEffect } from 'react';
import { Table, Cells } from '../../design/table2';
import { ChartData } from '../data/common';
import { DataColumn, FormatHint } from '../../types';
import { getNullFillerRow, getRowDataSets } from './data';
import { DataSet } from '../../design/charts';
import { useMobile } from '../../context/mobile/mobile.provider';
import classNames from 'classnames';

const { Button } = actions;

type Props = {
  series: ChartData;
  summary?: ChartData;
  xColumn: DataColumn;
  chartFullscreen?: boolean;
};
const TimeSeriesTable = forwardRef<HTMLDivElement, Props>(({ series, summary, xColumn, chartFullscreen }, ref) => {
  const [rowsToDisplay, setRowsToDisplay] = useState<number>(100);

  const rowDataSets = getRowDataSets({ series, summary });
  const summaryFiller = getNullFillerRow(summary);

  const { isMobile } = useMobile();
  const [isLandscape, setIsLandscape] = useState(
    typeof window !== 'undefined' && window.innerWidth > window.innerHeight
  );
  const rowCount = rowDataSets.length;
  const includeTotals =
    series.totalsDataSet !== undefined &&
    ![FormatHint.Percentage, FormatHint.Index].includes(series.yColumn.formatHint);

  useEffect(() => {
    const handleOrientationChange = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
    };

    window.addEventListener('resize', handleOrientationChange);
    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      window.removeEventListener('resize', handleOrientationChange);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, []);

  const handleLoadMore = () => {
    const newAmount = rowsToDisplay + 100;
    if (newAmount > rowCount) {
      setRowsToDisplay(rowCount);
    } else {
      setRowsToDisplay(newAmount);
    }
  };

  return (
    <div
      ref={ref}
      className={classNames('relative w-full', {
        'chart-h-70vh mt-7': isMobile && !(isLandscape || chartFullscreen),
        'chart-h-90vh mt-7': isMobile && (isLandscape || chartFullscreen),
      })}
    >
      <Table>
        <Table.Header withRows={false} isStickyTop={isMobile}>
          <tr>
            <Table.Column rowSpan={2} isStickyLeft={isMobile}>
              {xColumn.label}
            </Table.Column>
            <SeriesHeadersTop series={series} summary={summary} />
          </tr>
          <tr>
            <SeriesHeadersBottom series={series} summary={summary} />
          </tr>
        </Table.Header>
        <Table.Body>
          {includeTotals && (
            <Table.Row important>
              <Table.Cell align="left" isStickyLeft={isMobile}>
                Market
              </Table.Cell>
              <SeriesCells
                series={series.totalsDataSet}
                summary={summary?.totalsDataSet}
                uniqueId="totals"
                summaryFiller={summaryFiller}
              />
            </Table.Row>
          )}
          {rowDataSets.slice(0, rowsToDisplay).map(set => (
            <Table.Row key={`r_${set.uniqueId}`}>
              <Table.Cell align="left" isStickyLeft={isMobile}>
                {set.label}
              </Table.Cell>
              <SeriesCells
                series={set.seriesSet}
                summary={set.summarySet}
                uniqueId={set.uniqueId}
                summaryFiller={summaryFiller}
              />
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      <div className="py-4 flex items-center gap-4">
        <span className="">
          {rowsToDisplay > rowCount && `${rowCount} out of ${rowCount} items`}
          {rowsToDisplay <= rowCount && `${rowsToDisplay} out of ${rowCount} items`}
        </span>
        {rowsToDisplay <= rowCount && <Button onClick={handleLoadMore}>Load more</Button>}
      </div>
    </div>
  );
});

type SeriesHeadersProps = {
  series: ChartData;
  summary?: ChartData;
};
function SeriesHeadersTop({ series, summary }: SeriesHeadersProps) {
  return (
    <>
      {summary && (
        <Table.Column align="center" borders="lrb" colSpan={summary.dataLabels.length}>
          {summary.yColumn.label}
        </Table.Column>
      )}
      <Table.Column align="center" borders="lrb" colSpan={series.dataLabels.length}>
        {series.yColumn.label}
      </Table.Column>
    </>
  );
}

function SeriesHeadersBottom({ series, summary }: SeriesHeadersProps) {
  return (
    <>
      {summary &&
        summary.dataLabels.map((label, idx) => (
          <Table.Column
            align="center"
            key={`sum_${idx}`}
            borders={`b${idx === 0 ? 'l' : idx === summary.dataLabels.length - 1 ? 'r' : ''}`}
          >
            <HeaderLabel label={label} />
          </Table.Column>
        ))}
      {series.dataLabels.map((label, idx) => (
        <Table.Column
          align="center"
          key={`sr_${idx}`}
          borders={`b${idx === 0 ? 'l' : idx === series.dataLabels.length - 1 ? 'r' : ''}`}
        >
          <HeaderLabel label={label} />
        </Table.Column>
      ))}
    </>
  );
}

type LabelProps = {
  label: string | string[];
};
function HeaderLabel({ label }: LabelProps) {
  if (Array.isArray(label)) {
    return (
      <>
        {label.map((l, idx) => (
          <span key={`${l}-${idx}`} className="block nowrap">
            {l}
          </span>
        ))}
      </>
    );
  }

  return <span className="block nowrap">{label}</span>;
}

type SeriesCellsProps = {
  uniqueId: string;
  series?: DataSet;
  summary?: DataSet;
  summaryFiller: number[];
};

function SeriesCells({ uniqueId, series, summary, summaryFiller }: SeriesCellsProps) {
  return (
    <>
      {summary &&
        summary.values.formatted.map((value, idx) => (
          <Table.Cell
            borders={`b${idx === 0 ? 'l' : idx === summary.values.formatted.length - 1 ? 'r' : ''}`}
            key={`row_${uniqueId}_summary_${idx}`}
          >
            {value || '-'}
            <Cells.ValueChange value={summary.changes?.formatted[idx]} raw={summary.changes?.raw[idx]} />
          </Table.Cell>
        ))}
      {!summary &&
        summaryFiller.map((_value, idx) => (
          <Table.Cell
            borders={`b${idx === 0 ? 'l' : idx === summaryFiller.length - 1 ? 'r' : ''}`}
            key={`row_${uniqueId}_summary_${idx}`}
          >
            {'-'}
            <Cells.ValueChange value={''} raw={0} />
          </Table.Cell>
        ))}
      {series.values.formatted.map((value, idx) => (
        <Table.Cell
          borders={`b${idx === 0 ? 'l' : idx === series.values.formatted.length - 1 ? 'r' : ''}`}
          key={`row_${uniqueId}_series_${idx}`}
        >
          {value || '-'}
          <Cells.ValueChange value={series.changes?.formatted[idx]} raw={series.changes?.raw[idx]} />
        </Table.Cell>
      ))}
    </>
  );
}

export { TimeSeriesTable };
